<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { computed, ref, onMounted, watch } from 'vue'
import MediaIcon from '@/assets/mediaIcon.vue'
import DisposalIcon from '@/assets/disposalIcon.vue'
import costIcon from '@/assets/costIcon.vue'
import { useUserStore } from '@/stores/user'
import { TrendCharts, Histogram, UserFilled, HomeFilled } from '@element-plus/icons-vue'

// 定义子菜单项的结构
interface MenuChild {
  name: string
  routeName: string
  module?: string
}

// 定义父菜单项的结构
interface MenuItem {
  name: string
  icon: string
  children: MenuChild[]
  routeName?: string
  module?: string
}

type VisibleMenuItem = MenuItem & {
  visibleChildren: MenuChild[]
}

const router = useRouter()
const route = useRoute()

const activeMenu = ref('')
const openMenus = ref<string[]>([]) // 用于记录当前展开的子菜单
const userStore = useUserStore()

const menus: MenuItem[] = [
  {
    name: '首页',
    icon: 'home',
    children: [],
    routeName: 'Home',
  },
  {
    name: '监测中心',
    icon: 'monitor',
    children: [
      { name: '舆情看板', routeName: 'PublicopinionDashboard', module: 'monitorDashboard' },
      { name: '舆情详情', routeName: 'PublicopinionDetail', module: 'monitorDetails' },
    ],
  },
  {
    name: '任务中心',
    icon: 'platform',
    children: [
      { name: '内部详情', routeName: 'InternalDetails', module: 'taskInternal' },
      { name: '流程监听', routeName: 'ProcessMonitor', module: 'taskMonitor' },
      { name: '归档看板', routeName: 'ArchiveDashboard', module: 'taskArchive' },
    ],
  },
  {
    name: '追责中心',
    icon: 'execute',
    children: [
      { name: '追责看板', routeName: 'AccountabilityDashboard', module: 'accountabilityDashboard' },
      { name: '整改任务', routeName: 'Rectification', module: 'accountabilityProcess' },
      { name: '追责列表', routeName: 'AccountabilityList', module: 'accountabilityWorkorder' },
    ],
  },
  {
    name: '媒体资源库',
    icon: 'media',
    children: [
      { name: '媒体资源看板', routeName: 'MediaResourceDashboard', module: 'mediaResources' },
      { name: '媒体报道', routeName: 'MediaReport', module: 'mediaList' },
      { name: '媒体管理', routeName: 'MediaLibrary', module: 'mediaDetails' },
      { name: '监管部门', routeName: 'CompetentAuthority', module: 'mediaAuthority' },
      { name: '合作服务商', routeName: 'ServiceProvider', module: 'mediaServicer' },
    ],
  },
  {
    name: '成本预算库',
    icon: 'cost',
    children: [
      { name: '成本看板', routeName: 'CostBudgetDashboard', module: 'costDashboard' },
      { name: '成本支出列表', routeName: 'ExpenseList', module: 'costDetails' },
      { name: '预算分配', routeName: 'BudgetAllocation', module: 'budgetAllocation' },
    ],
  },
  {
    name: '用户管理',
    icon: 'user',
    children: [
      { name: '个人信息', routeName: 'PersonalInformation' },
      { name: '权限管理', routeName: 'AuthorityManagement' },
    ],
  },
]

const routeToMenuMap: Record<string, string> = {
  PublicopinionContent: 'PublicopinionDetail',
  InternalDetailsNext: 'InternalDetails',
  ArchiveDetail: 'ArchiveDashboard',
}

const canViewModule = (moduleName?: string) => {
  if (!moduleName) return true
  return userStore.hasModuleAuth(moduleName)
}

const visibleMenus = computed<VisibleMenuItem[]>(() =>
  menus
    .filter((menu) => canViewModule(menu.module))
    .map((menu) => ({
      ...menu,
      visibleChildren: menu.children.filter((child) => canViewModule(child.module)),
    }))
    .filter((menu) => menu.children.length === 0 || menu.visibleChildren.length > 0),
)

function findMenuByRouteName(
  routeName: string,
): { menuName: string; parentName: string | null } | null {
  const mappedRouteName = routeToMenuMap[routeName] || routeName
  for (const menu of menus) {
    if (menu.routeName === mappedRouteName) {
      return { menuName: menu.name, parentName: null }
    }
    for (const child of menu.children) {
      if (child.routeName === mappedRouteName) {
        return { menuName: child.name, parentName: menu.name }
      }
    }
  }
  return null
}

function updateMenuFromRoute() {
  const currentRouteName = route.name as string
  if (!currentRouteName) return

  const result = findMenuByRouteName(currentRouteName)
  if (result) {
    activeMenu.value = result.parentName || result.menuName
  }
}

// 展开/折叠逻辑
function toggleMenu(name: string) {
  if (openMenus.value.includes(name)) {
    openMenus.value = []
  } else {
    openMenus.value = [name]
  }
}

function selectMenu(menuItem: MenuItem) {
  if (!canViewModule(menuItem.module)) return
  if (menuItem.routeName) {
    router.push({ name: menuItem.routeName })
    openMenus.value = []
  }
}

// 选择子菜单跳转
function selectChildMenu(child: MenuChild) {
  if (!canViewModule(child.module)) return
  if (child.routeName) {
    router.push({ name: child.routeName })
  }
}

onMounted(() => {
  updateMenuFromRoute()
})

watch(
  () => route.name,
  () => {
    updateMenuFromRoute()
  },
)
</script>

<template>
  <div class="sidebar">
    <div class="sidebar-header">
      <img src="../assets/啄木鸟头像无文字.jpg" alt="" />
    </div>
    <ul class="menu-list">
      <li v-for="menu in visibleMenus" :key="menu.name" class="menu-container">
        <div
          class="menu-item"
          :class="{
            active: activeMenu === menu.name,
          }"
          @click="menu.visibleChildren.length ? toggleMenu(menu.name) : selectMenu(menu)"
        >
          <span class="icon">
            <el-icon v-if="menu.icon === 'home'" style="font-size: 20px"><HomeFilled /></el-icon>
            <el-icon v-else-if="menu.icon === 'monitor'" style="font-size: 20px"
              ><TrendCharts
            /></el-icon>
            <DisposalIcon
              v-else-if="menu.icon === 'platform'"
              style="font-size: 20px; width: 20px; height: 20px"
            />
            <el-icon v-else-if="menu.icon === 'execute'" style="font-size: 20px"
              ><Histogram
            /></el-icon>
            <MediaIcon
              v-else-if="menu.icon === 'media'"
              style="font-size: 20px; width: 20px; height: 20px"
            />
            <costIcon
              v-else-if="menu.icon === 'cost'"
              style="font-size: 20px; width: 20px; height: 20px"
            />
            <el-icon v-else-if="menu.icon === 'user'" style="font-size: 20px"
              ><UserFilled
            /></el-icon>
          </span>
          <span class="text">{{ menu.name }}</span>
        </div>

        <ul
          v-if="menu.visibleChildren.length && openMenus.includes(menu.name)"
          class="inline-submenu"
        >
          <li
            v-for="child in menu.visibleChildren"
            :key="child.name"
            :class="{
              'active-child':
                route.name === child.routeName ||
                routeToMenuMap[route.name as string] === child.routeName,
            }"
            @click.stop="selectChildMenu(child)"
          >
            {{ child.name }}
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.sidebar {
  width: 100px;
  background: #3f83f4;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
}

.sidebar-header {
  margin-top: 10px;
  margin-bottom: 20px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-header img {
  width: 100%;
  height: 100%;
  border-radius: 20%;
}

.menu-list {
  list-style: none;
  width: 100%;
  padding: 0 2px;
  box-sizing: border-box; /* 确保 padding 不会把侧边栏撑宽 */
  margin: 0;
  flex: 1;
  overflow-y: auto; /* 允许在内容过多时滚动 */
  scrollbar-width: none;
}

.menu-container {
  width: 100%;
  display: flex;
  flex-direction: column; /* 确保一级和二级菜单垂直排列 */
  margin-bottom: 12px; /* 增加整个菜单块之间的上下间距 */
}

.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: auto;
  padding: 12px 0;
  margin: 0;
  cursor: pointer;
  color: #fff;
  border-radius: 8px;
  transition:
    background 0.2s,
    color 0.2s;
}

.menu-item.active,
.menu-item:hover {
  color: #3f83f4;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
}

.icon {
  width: 24px;
  height: 24px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.menu-item.active .icon,
.menu-item:hover .icon {
  color: #3f83f4;
}

.text {
  font-size: 14px;
  color: #fff;
  line-height: 1.2;
}

.menu-item.active .text,
.menu-item:hover .text {
  color: #3f83f4;
}

/* 二级子菜单容器 */
.inline-submenu {
  list-style: none;
  padding: 0;
  margin: 6px 0 0 0; /* 与上方的一级菜单稍微拉开一点距离 */
  width: 100%;
  background: transparent;
}

/* 二级子菜单的每一项 (独立卡片) */
.inline-submenu li {
  padding: 8px 4px;
  color: #e0e0e0;
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.3;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.1);
  margin-bottom: 6px; /* 各个子菜单项之间的上下间隙 */
  transition:
    background 0.2s,
    color 0.2s;
}

/* 最后一个子菜单项不需要下边距 */
.inline-submenu li:last-child {
  margin-bottom: 0;
}

.inline-submenu li:hover,
.inline-submenu li.active-child {
  color: #fff;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.2); /* 悬浮和激活时的颜色变得更亮 */
}
</style>
